import { Button, Form, Input, QRCode, Typography } from "antd";
import { KeyOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface TotpSetupProps {
  otpauthUri: string;
  secret: string;
  loading?: boolean;
  submitLabel?: string;
  onConfirm: (totpCode: string) => void;
}

/**
 * First-time TOTP binding: render the otpauth URI as a QR (AntD's built-in
 * <QRCode>, no extra dep — same approach as coco-mgt's ga-rebind) plus the raw
 * secret for manual entry, and collect the first 6-digit code to confirm.
 */
export default function TotpSetup({
  otpauthUri,
  secret,
  loading = false,
  submitLabel = "Confirm and continue",
  onConfirm,
}: TotpSetupProps): JSX.Element {
  return (
    <Form
      layout="vertical"
      autoComplete="off"
      onFinish={(values: { totpCode: string }) => onConfirm(values.totpCode)}
    >
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <Text type="secondary">Scan this QR code with your authenticator app.</Text>
      </div>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <QRCode value={otpauthUri} size={200} style={{ margin: "0 auto" }} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Can&apos;t scan? Enter this key manually:
        </Text>
        <div
          style={{
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            padding: "8px 12px",
            fontFamily: "monospace",
            fontSize: 13,
            wordBreak: "break-all",
            marginTop: 4,
          }}
        >
          {secret}
        </div>
      </div>
      <Form.Item
        name="totpCode"
        rules={[
          { required: true, message: "Enter the 6-digit code" },
          { len: 6, message: "Code must be 6 digits" },
        ]}
      >
        <Input
          prefix={<KeyOutlined />}
          placeholder="6-digit code from your app"
          size="large"
          maxLength={6}
          inputMode="numeric"
          style={{ letterSpacing: 4, textAlign: "center", fontSize: 18 }}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {submitLabel}
        </Button>
      </Form.Item>
    </Form>
  );
}
